import '../AddFire.scss';
import React, { useEffect, useState, useRef } from "react";
import axios from 'axios';
import Spinner from '../../../Spinner.js';
import locationIcon from '../../../../assets/icons/location-icon.png';
import { SearchBox } from '@mapbox/search-js-react';
import mapboxgl from 'mapbox-gl';
import AddPerimeter from './AddPerimeter.js';
import AddFireForm from './AddFireForm.js';
import { useUserContext } from '../../../../contexts/UserContext';

export default function AddFire(props) {
    const {
        is3D,
        setIs3D
    } = useUserContext();

    const {
        setPanel,
        map, 
        draw
    } = props;

    const fireMarker = useRef(null);
    const locationRef = useRef(null);

    const [locationLoading, setLocationLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fire, setFire] = useState(null);
    const [stage, setStage] = useState('add-fire');
    const [perimeter, setPerimeter] = useState(null);

    useEffect(() => {
        const handleMapClick = (e) => {
            if (stage === 'add-fire') {
                const location = [e.lngLat.lng, e.lngLat.lat];
                
                if (fireMarker.current) {
                    fireMarker.current.remove();
                }
    
                const point = document.createElement('div');
                point.className = 'map-fire-marker';
                const marker = new mapboxgl.Marker(point).setLngLat(location).addTo(map.current);
                fireMarker.current = marker;
    
                const lat = fireMarker.current._lngLat.lat;
                const lng = fireMarker.current._lngLat.lng;
    
                setFire([lat, lng]);
            }
        };
    
        if (map?.current) {
            map.current.on('click', handleMapClick);
        }
    
        return () => {
            if (map?.current) {
                map.current.off('click', handleMapClick);
            }
        };
    }, [map, stage]);

    useEffect(() => {
       return () => {
        if (locationRef.current) {
            locationRef.current.remove();
        }

        if (fireMarker.current) {
            fireMarker.current.remove();
        }
       }
    }, []);
    
    const getLocation = () => {
        setError(false);
        setLocationLoading(true);
        if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const location = [position.coords.longitude, position.coords.latitude];
                 if (location) {
                    map.current.flyTo({ center: location, pitch: 0, zoom: 11 });

                    if (is3D) {
                        setIs3D(false);
                    }

                    if (locationRef.current) {
                        locationRef.current.remove();
                    }

                    const point = document.createElement('div');
                    point.className = 'map-current-location-marker';
                    const marker = new mapboxgl.Marker(point).setLngLat(location).addTo(map.current);
                    locationRef.current = marker;
                    setLocationLoading(false);
                }
            },
            (err) => {
                setLocationLoading(false);
                setError('Trouble getting your location. Please try again.');
            }
        );
        } else {
            setLocationLoading(false);
            setError('Trouble getting your location. Please try again.');
        }
    };

    const stageSwitch = (stage) => {
        switch (stage) {
            case 'add-fire':
                return (
                    <div className='add-fire-content-instructions'>
                        <div className='add-fire-content-instructions-instruction'>
                            <div className='add-fire-content-instructions-instruction-title'>Step 1: Choose location of fire</div>
                            <div className='add-fire-content-instructions-instruction-sub-title'>Option 1: Current Location</div>
                            {locationLoading ?
                            <div style={{ paddingTop: '1rem' }}>
                                <Spinner />
                            </div>
                            :
                            <div 
                                className='add-fire-content-instructions-instruction-current-location-btn'
                                onClick={getLocation}
                            >
                                <img src={locationIcon} className='add-fire-content-instructions-instruction-current-location-btn-icon'/>
                                <div 
                                    className='add-fire-content-instructions-instruction-current-location-btn-text'
                                >Use Current Location
                                </div>
                            </div>}
                            <div className='add-fire-content-instructions-instruction-sub-title'>Option 2: Search a location</div>
                            <SearchBox
                                accessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN} 
                                value=''
                                onRetrieve={(e) => {
                                    const location = e.features[0].geometry.coordinates;
                                    map.current.flyTo({center: location, zoom: 11, pitch: 0, offset: [250, 0]});

                                    if (is3D) {
                                        setIs3D(false);
                                    }
                                }}
                            />
                        </div>
                        <div className='add-fire-content-instructions-instruction'>
                            <div className='add-fire-content-instructions-instruction-title'>Step 2: Plot fire on map</div>
                            <div className='add-fire-content-instructions-instruction-sub-title'>Click a point on the map.</div>
                            {fire && 
                            <div 
                                style={{ marginTop: '0.4rem' , marginBottom: '0.2rem'}}
                                className='add-fire-content-instructions-instruction-sub-title'>{`Location selected: ${fire[0].toFixed(6)}, ${fire[1].toFixed(6)}`}</div>}
                            <button
                                className={fire ? 'add-fire-content-instructions-instruction-next-btn' : 'add-fire-content-instructions-instruction-disabled-btn'}
                                disabled={!fire}
                                onClick={() => {
                                    setStage('add-perimeter');
                                }}
                            >Next
                            </button>
                        </div>
                    </div>
                )
            case 'add-perimeter':
                return (
                    <AddPerimeter 
                        stage={stage}
                        setStage={setStage} 
                        map={map}
                        draw={draw}
                        perimeter={perimeter}
                        setPerimeter={setPerimeter}
                        fire={fire}
                        setPanel={setPanel}
                    />
                )
            case 'fire-form':
                return (
                   <AddFireForm
                        setStage={setStage} 
                        map={map}
                        draw={draw}
                        perimeter={perimeter}
                        setPerimeter={setPerimeter}
                        fire={fire}
                        setPanel={setPanel}
                    />
                )
        }
    }
    
    return (
        <div className='add-fire'>
            <div className='add-fire-title'>
            <div className='add-fire-title-text'>Add Fire</div>
            </div>
            <div className='add-fire-description'>Input a fire to the map by specifying its location and details, enabling real-time tracking and monitoring.</div>
            <div className='add-fire-content'>
                {stageSwitch(stage)}
            </div>
        </div>
    );
}